import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Radio, Mic, Loader2, Star, Clapperboard, Share2, Pencil, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import MediaFormModal from "@/components/enhancer/MediaFormModal";
import ReplayEditModal from "@/components/media/ReplayEditModal";
import api, { formatApiError } from "@/lib/api";
import { fmtDateTime, fmtDate } from "@/lib/format";
import { AccessBadge, shareItem } from "@/components/media/HubCard";

const field = "w-full rounded-xl border border-[#E5E0D8] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#144F75]";

function GoLiveForm({ events, onDone }) {
  const nav = useNavigate();
  const [title, setTitle] = useState("");
  const [eventId, setEventId] = useState("");
  const [access, setAccess] = useState("public");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const r = await api.post("/broadcasts", { title, event_id: eventId, access }); onDone?.(); nav(r.data.studio_path); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't start the broadcast"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="eyw-card p-5 space-y-3" data-testid="golive-form">
      <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2"><Radio size={18} className="text-red-600" /> Go live</h3>
      <select value={eventId} onChange={(e) => setEventId(e.target.value)} aria-label="Link to an event" data-testid="golive-event" className={field}>
        <option value="">Standalone broadcast (not linked to an event)</option>
        {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.title} · {fmtDate(ev.date)} {ev.start_time}</option>)}
      </select>
      {!eventId && (
        <>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Name your broadcast" maxLength={120} aria-label="Broadcast name" data-testid="golive-title" className={field} />
          <select value={access} onChange={(e) => setAccess(e.target.value)} aria-label="Who can watch" data-testid="golive-access" className={field}>
            <option value="public">Public — anyone can watch</option>
            <option value="ticket_or_members">Members only</option>
          </select>
        </>
      )}
      <button type="submit" disabled={busy || (!eventId && !title.trim())} data-testid="golive-submit" className="eyw-btn w-full justify-center !bg-red-600 disabled:opacity-50">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Clapperboard size={15} />} Open the studio
      </button>
    </form>
  );
}

function PublishToggle({ it, tid, refresh }) {
  const published = it.published !== false;
  const toggle = async () => {
    try { await api.post("/media-hub/items/publish", { source: it.source, id: it.id, published: !published }); toast.success(published ? "Moved to drafts — hidden from the Media page" : "Published on the Media page"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update"); }
  };
  return (
    <button onClick={toggle} data-testid={`bh-publish-${tid}`} title="Drafts stay saved but are hidden from the public Media page"
      className={`text-xs font-medium rounded-full px-2.5 py-1 border transition-colors ${published ? "border-[#5C8A72]/40 text-[#5C8A72] bg-[#5C8A72]/10" : "border-[#E5E0D8] text-[#8FA89B] bg-white"}`}>
      {published ? "Published" : "Draft"}
    </button>
  );
}

const ARCHIVE = ["replay", "podcast"];

function Row({ it, isAdmin, refresh, onEdit, onDelete }) {
  const feature = async () => {
    try { await api.post("/admin/media-hub/feature", { source: it.source, id: it.id, value: !it.featured }); toast.success(it.featured ? "Unfeatured" : "Featured on Media"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update"); }
  };
  const tid = `${it.source}-${it.id}`;
  const draft = it.published === false;
  const archived = ARCHIVE.includes(it.kind);
  return (
    <li className={`flex flex-wrap items-center gap-3 bg-white rounded-xl border border-[#E5E0D8] px-4 py-3 ${draft ? "opacity-70" : ""}`} data-testid={`bh-item-${tid}`}>
      <div className="flex-1 min-w-[200px]">
        <p className="font-medium text-[#1A242B]">{it.title}</p>
        <p className="text-xs text-[#8FA89B]">{it.host ? `${it.host} · ` : ""}{it.date ? fmtDateTime(it.date) : ""}</p>
      </div>
      <AccessBadge level={it.access} testid={`bh-access-${tid}`} />
      {(archived || it.source === "media") && <PublishToggle it={it} tid={tid} refresh={refresh} />}
      {it.source === "event" && it.kind !== "replay" && <Link to={it.url.replace(/\/watch$/, "/studio")} data-testid={`bh-studio-${tid}`} className="text-sm text-[#144F75] hover:underline">Studio</Link>}
      <Link to={it.url} data-testid={`bh-open-${tid}`} className="text-sm text-[#144F75] hover:underline">Open</Link>
      <button onClick={() => shareItem(it.url)} aria-label="Copy link" data-testid={`bh-share-${tid}`} className="text-[#144F75]"><Share2 size={14} /></button>
      {(archived || it.source === "media") && (
        <>
          <button onClick={() => onEdit(it)} aria-label="Edit" data-testid={`bh-edit-${tid}`} className="text-[#144F75] hover:text-[#0f3b58]"><Pencil size={15} /></button>
          <button onClick={() => onDelete(it)} aria-label="Delete" data-testid={`bh-delete-${tid}`} className="text-[#8FA89B] hover:text-red-600"><Trash2 size={15} /></button>
        </>
      )}
      {isAdmin && <button onClick={feature} data-testid={`bh-feature-${tid}`} aria-label="Feature" title="Featured items show first on the Media page" className={it.featured ? "text-[#D98E73]" : "text-[#8FA89B]"}><Star size={15} fill={it.featured ? "currentColor" : "none"} /></button>}
    </li>
  );
}

function DeleteDialog({ item, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await api.delete(item.source === "media" ? `/media/${item.id}` : `/media-hub/replays/${item.id}`);
      toast.success("Deleted permanently"); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't delete"); }
    finally { setBusy(false); onClose(); }
  };
  return (
    <AlertDialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid="bh-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete “{item?.title}” permanently?</AlertDialogTitle>
          <AlertDialogDescription>
            {item?.source === "media" ? "The video and its uploaded file are removed from the Media page and search results right away. This can't be undone."
              : "The replay recording link is removed from the Media page right away. The event itself, its tickets and bookings stay. This can't be undone."}
            {" "}To hide it without deleting, switch it to Draft instead.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="bh-delete-cancel">Keep it</AlertDialogCancel>
          <AlertDialogAction onClick={run} disabled={busy} data-testid="bh-delete-confirm" className="bg-red-600 hover:bg-red-700">Delete permanently</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const GROUPS = [["live", "Live now"], ["upcoming", "Upcoming live events"], ["replay", "Replays"], ["podcast", "Podcast episodes"]];

export const BroadcastingHub = ({ onNewEpisode }) => {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const { data, isLoading, isError } = useQuery({ queryKey: ["media-hub-manage"], queryFn: () => api.get("/media-hub/manage").then((r) => r.data), refetchInterval: 30000 });
  const refresh = () => { ["media-hub-manage", "enh-media", "media-hub"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); };
  const newEpisode = onNewEpisode || (() => setEditing({ source: "media", edit: {} }));
  return (
    <section className="mb-10" data-testid="broadcasting-hub">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B]">Media & Broadcasting</h2>
          <p className="text-sm text-[#4A5D6B]">Go live, publish episodes, and edit, unpublish or delete past videos — everything shows on the <Link to="/media" className="underline">Media page</Link>.</p>
        </div>
        <button onClick={newEpisode} data-testid="bh-new-episode" className="eyw-btn !py-2 text-sm"><Mic size={15} /> New episode</button>
      </div>
      <div className="grid lg:grid-cols-[360px_minmax(0,1fr)] gap-6">
        <GoLiveForm events={data?.events || []} onDone={refresh} />
        <div className="space-y-6" data-testid="bh-lists">
          {isLoading && <Loader2 className="animate-spin text-[#144F75]" />}
          {isError && <p className="text-sm text-red-600" data-testid="bh-error">Couldn't load your media. Refresh to try again.</p>}
          {data && data.items.length === 0 && <p className="text-sm text-[#4A5D6B]" data-testid="bh-empty">Nothing yet — go live or publish your first episode.</p>}
          {data && GROUPS.map(([k, label]) => {
            const xs = data.items.filter((i) => i.kind === k);
            return xs.length > 0 && (
              <div key={k} data-testid={`bh-group-${k}`}>
                <h3 className="text-sm font-semibold text-[#1A242B] mb-2">{label} ({xs.length})</h3>
                <ul className="space-y-2">{xs.map((it) => <Row key={`${it.source}-${it.id}`} it={it} isAdmin={data.is_admin} refresh={refresh} onEdit={setEditing} onDelete={setDeleting} />)}</ul>
              </div>
            );
          })}
        </div>
      </div>
      {editing?.source === "media" && <MediaFormModal item={editing.edit?.id ? editing.edit : null} onClose={() => setEditing(null)} onSaved={refresh} />}
      {editing?.source === "event" && <ReplayEditModal item={editing} offerings={data?.offerings || []} onClose={() => setEditing(null)} onSaved={refresh} />}
      <DeleteDialog item={deleting} onClose={() => setDeleting(null)} onDone={refresh} />
    </section>
  );
};
